import { useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Camera, MapPin, Sparkle, House, WarningCircle, CheckCircle } from "@phosphor-icons/react";

export default function PropertyFingerprint() {
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  const onFile = async (file) => {
    if (!file) return;
    setPreviewUrl(URL.createObjectURL(file));
    setScanning(true);
    const tid = toast.loading("Reading the building…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/property-fingerprint", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.dismiss(tid);
      setResult(r.data);
    } catch (e) {
      toast.dismiss(tid);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Scan failed");
    } finally { setScanning(false); }
  };

  const fp = result?.fingerprint || {};
  const match = result?.match;

  return (
    <AdminShell>
      <div className="max-w-2xl mx-auto p-4 space-y-4" data-testid="property-fp-page">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-700">Field Mode</div>
          <h1 className="font-display font-black text-3xl uppercase tracking-tight flex items-center gap-2">
            <Sparkle weight="fill" size={26}/> Property Fingerprint
          </h1>
          <div className="text-xs text-slate-500 mt-1">Snap the front of a house — AI matches it to your book or flags it as new.</div>
        </div>

        <label className={`block p-6 border-4 border-dashed border-fuchsia-500 bg-fuchsia-50 hover:bg-fuchsia-100 cursor-pointer text-center ${scanning ? "opacity-60 cursor-wait" : ""}`}
          data-testid="fp-scan-label">
          <Camera weight="fill" size={40} className="mx-auto text-fuchsia-600"/>
          <div className="mt-2 font-display font-black uppercase tracking-widest">Tap to snap the house</div>
          <div className="text-[11px] text-slate-500">Front view · get the numbers in frame if possible</div>
          <input type="file" accept="image/*" capture="environment" hidden disabled={scanning}
            data-testid="fp-file-input"
            onChange={e => onFile(e.target.files?.[0])}/>
        </label>

        {previewUrl && (
          <img src={previewUrl} alt="" className="w-full max-h-72 object-cover border-2 border-slate-900"/>
        )}

        {result && (
          <div data-testid="fp-result" className="space-y-3">
            <div className={`p-4 border-4 ${match?.matched ? "border-emerald-600 bg-emerald-50" : "border-amber-500 bg-amber-50"} shadow-[6px_6px_0_#0f172a]`}>
              {match?.matched ? (
                <>
                  <div className="flex items-center gap-2 text-emerald-800">
                    <CheckCircle weight="fill" size={20}/>
                    <div className="font-display font-black text-xl">Match found</div>
                  </div>
                  <div className="mt-1 font-black">{match.address}</div>
                  <div className="text-xs text-slate-600 mt-0.5">{match.unit_count} unit{match.unit_count === 1 ? "" : "s"} on file</div>
                  <div className="mt-2 flex gap-2 flex-wrap">
                    <Link to={`/admin/properties/${encodeURIComponent(match.address)}`}
                      data-testid="fp-open-property"
                      className="h-9 px-3 border-2 border-emerald-800 bg-emerald-700 hover:bg-emerald-600 text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1">
                      Open Property →
                    </Link>
                    {match.unit_count > 1 && (
                      <Link to={`/admin/properties/${encodeURIComponent(match.address)}/board`}
                        className="h-9 px-3 border-2 border-cyan-800 bg-cyan-600 hover:bg-cyan-500 text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1">
                        Multi-Unit Board →
                      </Link>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-amber-800">
                    <WarningCircle weight="fill" size={20}/>
                    <div className="font-display font-black text-xl">NEW · not in your book</div>
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    {fp.street_number ? `AI read: ${fp.street_number} ${fp.street_name || ""}` : "Couldn't read a house number"}
                  </div>
                </>
              )}
            </div>

            <div className="p-3 bg-white border-2 border-slate-300 text-xs space-y-1">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">What AI saw</div>
              <Row label="Number" value={fp.street_number}/>
              <Row label="Street" value={fp.street_name}/>
              <Row label="City" value={fp.city}/>
              <Row label="Style" value={fp.style}/>
              <Row label="Stories" value={fp.stories}/>
              <Row label="Units guess" value={fp.unit_count_guess}/>
              <Row label="Distinctive" value={fp.distinguishing}/>
              <div className="pt-1 border-t border-slate-100 flex items-center gap-1 text-[10px] font-black uppercase tracking-widest">
                <span className="text-slate-500">Confidence</span>
                <span className={`px-1.5 py-0.5 ${fp.confidence === "high" ? "bg-emerald-600 text-white" : fp.confidence === "low" ? "bg-red-600 text-white" : "bg-amber-500 text-white"}`}>
                  {fp.confidence || "?"}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-slate-500">{label}</span>
    <span className="font-bold text-right truncate ml-2 max-w-[60%]">{value || "—"}</span>
  </div>
);
